import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calculator, FileDown, FileSpreadsheet, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDateHeure, formatLitres, formatMontant, formatNombre } from '../../lib/formats';
import { Aide } from '../ui/aide';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { StatutBadge } from '../ui/StatutBadge';
import { SearchableSelect } from '../ui/searchable-select';
import { LoadingState } from '../ui/loading-state';
import { EmptyState } from '../ui/empty-state';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { StationPicker, useDroitsStation, useStations } from './StationPicker';

/**
 * Relevé du gérant d'une station confiée (lot 034) : par produit, litres
 * vendus, commission, franchise de coulage, manquant, retenue, net. Le manquant
 * et le prix de revient peuvent être saisis avant de figer ; « Recalculer »
 * demande au serveur le nouveau relevé (aucun calcul ici). Figé, le relevé ne
 * change plus ; l'administrateur peut l'annuler avec un motif.
 */

const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];
const OPTIONS_MOIS = MOIS.map((nom, index) => ({ value: String(index + 1), label: nom }));

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground';

/** Mois précédent : le dernier mois terminé. */
const moisPrecedent = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return { annee: date.getFullYear(), mois: date.getMonth() + 1 };
};

const ManagerStatement = () => {
  const { canSeeMoney } = useAuth();
  const droits = useDroitsStation();
  const { stations } = useStations();
  const confiees = useMemo(() => stations.filter((station) => station.operating_mode === 'MANAGED'), [stations]);
  const [stationId, setStationId] = useState('');
  const [periode, setPeriode] = useState(moisPrecedent());
  const [releve, setReleve] = useState(null);
  const [saisies, setSaisies] = useState({});
  const [figes, setFiges] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState('');
  const [annulation, setAnnulation] = useState({ ouverte: false, motif: '' });

  const station = confiees.find((ligne) => ligne.id === stationId) || null;

  const charger = useCallback(async () => {
    if (!stationId) return;
    setChargement(true);
    try {
      const [courant, lesFiges] = await Promise.all([
        stationNetworkApi.statements.get(stationId, periode.annee, periode.mois),
        stationNetworkApi.statements.list({ station_id: stationId }),
      ]);
      setReleve(courant);
      setFiges(lesFiges || []);
      setSaisies({});
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger le relevé'));
      setReleve(null);
    } finally {
      setChargement(false);
    }
  }, [stationId, periode]);

  useEffect(() => {
    charger();
  }, [charger]);

  const saisir = (productId, cle) => (event) => {
    const valeur = event.target.value;
    setSaisies((avant) => ({ ...avant, [productId]: { ...(avant[productId] || {}), [cle]: valeur } }));
  };

  const surcharges = useMemo(() => {
    const shortages = [];
    const unitCosts = [];
    Object.entries(saisies).forEach(([productId, valeurs]) => {
      if (valeurs.liters !== undefined && valeurs.liters !== '') shortages.push({ productId, liters: Number(valeurs.liters) });
      if (valeurs.unitCost !== undefined && valeurs.unitCost !== '') unitCosts.push({ productId, unitCost: Number(valeurs.unitCost) });
    });
    return { shortages, unitCosts };
  }, [saisies]);
  const modifie = surcharges.shortages.length + surcharges.unitCosts.length > 0;

  const recalculer = async () => {
    setEnCours('calcul');
    try {
      setReleve(await stationNetworkApi.statements.preview(stationId, { year: periode.annee, month: periode.mois, ...surcharges }));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de recalculer le relevé'));
    } finally {
      setEnCours('');
    }
  };

  const figer = async () => {
    setEnCours('figement');
    try {
      const fige = await stationNetworkApi.statements.freeze(stationId, { year: periode.annee, month: periode.mois, ...surcharges });
      setReleve(fige);
      toast.success('Relevé figé');
      setFiges((await stationNetworkApi.statements.list({ station_id: stationId })) || []);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de figer le relevé'));
    } finally {
      setEnCours('');
    }
  };

  const annuler = async () => {
    setEnCours('annulation');
    try {
      await stationNetworkApi.statements.void(releve.id, annulation.motif.trim());
      toast.success('Relevé annulé');
      setAnnulation({ ouverte: false, motif: '' });
      await charger();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’annuler le relevé'));
    } finally {
      setEnCours('');
    }
  };

  const telecharger = async (format) => {
    setEnCours(format);
    try {
      await stationNetworkApi.statements.download(stationId, periode.annee, periode.mois, format);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de télécharger le fichier'));
    } finally {
      setEnCours('');
    }
  };

  if (!canSeeMoney) return null;

  const fige = Boolean(releve?.frozen);
  const peutFiger = droits.peutEcrire('reports') && releve && !fige && releve.missing_costs.length === 0;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-foreground">Station</span>
          <StationPicker stations={confiees} value={stationId} onChange={setStationId} />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-foreground">Mois</span>
          <SearchableSelect
            value={String(periode.mois)}
            onValueChange={(valeur) => setPeriode((avant) => ({ ...avant, mois: Number(valeur) }))}
            options={OPTIONS_MOIS}
            aria-label="Mois"
            className="h-9 w-40"
          />
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="releve-annee" className="text-sm font-medium text-foreground">
            Année
          </label>
          <Input
            id="releve-annee"
            type="number"
            min="2000"
            max="2200"
            value={periode.annee}
            onChange={(event) => event.target.value && setPeriode((avant) => ({ ...avant, annee: Number(event.target.value) }))}
            className="h-9 w-24 text-right tabular-nums"
          />
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={!releve || Boolean(enCours)} onClick={() => telecharger('pdf')}>
            <FileDown className="h-4 w-4" aria-hidden="true" />
            PDF
          </Button>
          <Button type="button" variant="outline" disabled={!releve || Boolean(enCours)} onClick={() => telecharger('xlsx')}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            Excel
          </Button>
        </div>
      </div>

      {!station ? (
        <EmptyState variant="empty" title="Aucune station confiée à un gérant" className="border-0" />
      ) : chargement && !releve ? (
        <LoadingState shape="table" />
      ) : !releve ? null : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {fige ? (
              <StatutBadge domaine="station_releve_gerant" code="FROZEN" />
            ) : (
              <Badge variant="warning">En attente de validation</Badge>
            )}
            {fige && releve.frozen_at && <span className="text-xs text-muted-foreground">{formatDateHeure(releve.frozen_at)}</span>}
            {releve.missing_costs.length > 0 && <Badge variant="destructive">Prix de revient manquant : {releve.missing_costs.join(', ')}</Badge>}
            <Aide titre="Relevé du gérant">
              Commission = litres vendus × commission au litre. Franchise = litres vendus × franchise ÷ 1 000. Retenue =
              (manquant − franchise) × prix de revient, si le manquant dépasse la franchise.
            </Aide>
            <div className="ml-auto flex flex-wrap gap-2">
              {!fige && (
                <Button type="button" variant="outline" disabled={!modifie || Boolean(enCours)} onClick={recalculer}>
                  <Calculator className="h-4 w-4" aria-hidden="true" />
                  Recalculer
                </Button>
              )}
              {peutFiger && (
                <Button type="button" disabled={Boolean(enCours)} onClick={figer}>
                  <Lock className="h-4 w-4" aria-hidden="true" />
                  Figer le relevé
                </Button>
              )}
              {fige && droits.estAdministrateur && (
                <Button type="button" variant="outline" onClick={() => setAnnulation({ ouverte: true, motif: '' })}>
                  Annuler le relevé
                </Button>
              )}
            </div>
          </div>

          <section className="overflow-x-auto rounded-xl border border-border bg-card px-1 py-2 shadow-sm">
            {releve.lines.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucune vente ce mois-ci</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Produit</th>
                    <th className="text-right">Litres vendus</th>
                    <th className="text-right">Commission</th>
                    <th className="text-right">Franchise</th>
                    <th className="text-right">Manquant</th>
                    <th className="text-right">Au-delà</th>
                    <th className="text-right">Prix de revient</th>
                    <th className="text-right">Retenue</th>
                    <th className="text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {releve.lines.map((ligne) => (
                    <tr key={ligne.product_id}>
                      <td className="font-medium">{ligne.product_name || 'Produit à préciser'}</td>
                      <td className="text-right tabular-nums">{formatLitres(ligne.litres_sold, 2)}</td>
                      <td className="text-right tabular-nums">{formatMontant(ligne.commission)}</td>
                      <td className="text-right tabular-nums">{formatLitres(ligne.allowance_liters, 2)}</td>
                      <td className="text-right">
                        {fige ? (
                          <span className="tabular-nums">{formatLitres(ligne.shortage_liters, 2)}</span>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Manquant ${ligne.product_name || ''}`}
                            value={saisies[ligne.product_id]?.liters ?? ligne.shortage_liters}
                            onChange={saisir(ligne.product_id, 'liters')}
                            className="ml-auto h-8 w-28 text-right tabular-nums"
                          />
                        )}
                      </td>
                      <td className="text-right tabular-nums">{formatLitres(ligne.excess_liters, 2)}</td>
                      <td className="text-right">
                        {fige ? (
                          <span className="tabular-nums">{ligne.unit_cost === null || ligne.unit_cost === undefined ? '—' : formatNombre(ligne.unit_cost, 2)}</span>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Prix de revient ${ligne.product_name || ''}`}
                            value={saisies[ligne.product_id]?.unitCost ?? ligne.unit_cost ?? ''}
                            onChange={saisir(ligne.product_id, 'unitCost')}
                            className="ml-auto h-8 w-28 text-right tabular-nums"
                          />
                        )}
                      </td>
                      <td className="text-right tabular-nums">{formatMontant(ligne.withholding)}</td>
                      <td className="text-right font-semibold tabular-nums">{formatMontant(ligne.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td>Total</td>
                    <td className="text-right tabular-nums">{formatLitres(releve.totals.litres_sold, 2)}</td>
                    <td className="text-right tabular-nums">{formatMontant(releve.totals.commission)}</td>
                    <td colSpan={4} />
                    <td className="text-right tabular-nums">{formatMontant(releve.totals.withholding)}</td>
                    <td className="text-right tabular-nums">{formatMontant(releve.totals.net)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </section>

          {figes.length > 0 && (
            <section className="overflow-x-auto rounded-xl border border-border bg-card px-1 py-2 shadow-sm">
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Mois</th>
                    <th className="text-left">État</th>
                    <th className="text-right">Commission</th>
                    <th className="text-right">Retenue</th>
                    <th className="text-right">Net</th>
                    <th className="text-left">Figé le</th>
                  </tr>
                </thead>
                <tbody>
                  {figes.map((ligne) => (
                    <tr key={ligne.id}>
                      <td className="font-medium">{ligne.period_label}</td>
                      <td>
                        <StatutBadge domaine="station_releve_gerant" code={ligne.status} />
                        {ligne.void_reason && <span className="ml-2 text-xs text-muted-foreground">{ligne.void_reason}</span>}
                      </td>
                      <td className="text-right tabular-nums">{formatMontant(ligne.totals.commission)}</td>
                      <td className="text-right tabular-nums">{formatMontant(ligne.totals.withholding)}</td>
                      <td className="text-right tabular-nums">{formatMontant(ligne.totals.net)}</td>
                      <td className="whitespace-nowrap">{formatDateHeure(ligne.frozen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      <Dialog open={annulation.ouverte} onOpenChange={(ouverte) => !ouverte && setAnnulation({ ouverte: false, motif: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Annuler le relevé figé</DialogTitle>
          </DialogHeader>
          <div className="grid gap-1.5">
            <label htmlFor="releve-motif" className="text-sm font-medium text-foreground">
              Motif
            </label>
            <Input id="releve-motif" value={annulation.motif} maxLength={500} onChange={(event) => setAnnulation((avant) => ({ ...avant, motif: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAnnulation({ ouverte: false, motif: '' })}>
              Fermer
            </Button>
            <Button type="button" variant="destructive" disabled={annulation.motif.trim().length < 3 || enCours === 'annulation'} onClick={annuler}>
              Annuler le relevé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerStatement;
